import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";

export type SsoPlatform = "WEB" | "IOS" | "ANDROID";

export const SSO_PLATFORM_LABELS: Record<SsoPlatform, string> = {
  WEB: "Web",
  IOS: "iOS",
  ANDROID: "Android",
};

export type SsoTokenEndpointAuthMethod = "client_secret_basic" | "client_secret_post" | "none";

export type SsoProtocol = "OIDC" | "SAML";

export const SSO_PROTOCOL_LABELS: Record<SsoProtocol, string> = {
  OIDC: "OpenID Connect",
  SAML: "SAML 2.0",
};

/** A logo the API serves for the login page, offered as a preset when configuring a provider. */
export interface SsoIconOption {
  name: string;
  url: string;
}

/** Image types the API accepts on upload. */
export const SSO_ICON_UPLOAD_ACCEPT = "image/png,image/jpeg,image/svg+xml,image/webp";

export interface SsoProviderInput {
  name: string;
  protocol: SsoProtocol;
  /** Absolute https URL of the logo shown beside "Continue with …"; blank shows the generic sign-in icon. */
  iconUrl: string;
  /** The OpenID Connect issuer, or for SAML the identity provider's entity ID from its metadata. */
  issuerUri: string;
  /** SAML only: the identity provider's federation metadata URL. */
  samlMetadataUrl: string;
  clientId: string;
  /** Required when creating a confidential client. Blank when editing keeps the stored secret. */
  clientSecret: string;
  tokenEndpointAuthMethod: SsoTokenEndpointAuthMethod;
  supportsWeb: boolean;
  supportsIos: boolean;
  supportsAndroid: boolean;
  /** Optional. Blank means the platform's callback from the API allowlist is used. */
  redirectUri: string;
  enabled: boolean;
}

/** The API never returns the secret itself, only whether one is stored. */
export interface CustomerSsoProvider extends Omit<
  SsoProviderInput,
  "clientSecret" | "redirectUri" | "samlMetadataUrl" | "iconUrl"
> {
  id: number;
  customerId: number;
  version: number;
  clientSecretConfigured: boolean;
  redirectUri: string | null;
  samlMetadataUrl: string | null;
  iconUrl: string | null;
}

type SsoProviderBody = Omit<
  SsoProviderInput,
  | "clientSecret"
  | "redirectUri"
  | "clientId"
  | "tokenEndpointAuthMethod"
  | "samlMetadataUrl"
  | "iconUrl"
> & {
  clientId?: string;
  clientSecret?: string;
  tokenEndpointAuthMethod?: SsoTokenEndpointAuthMethod;
  samlMetadataUrl?: string;
  redirectUri: string | null;
  iconUrl: string | null;
};

export const platformsOf = (provider: {
  supportsWeb: boolean;
  supportsIos: boolean;
  supportsAndroid: boolean;
}): SsoPlatform[] => {
  const platforms: SsoPlatform[] = [];
  if (provider.supportsWeb) platforms.push("WEB");
  if (provider.supportsIos) platforms.push("IOS");
  if (provider.supportsAndroid) platforms.push("ANDROID");
  return platforms;
};

export interface SsoSetup {
  /** Allowed callbacks per platform; the first is the default. Each must be registered with the IdP. */
  redirectUris: Partial<Record<SsoPlatform, string[]>>;
  /** The SAML service provider this API environment presents; register these with SAML identity providers. */
  samlEntityId?: string;
  samlAssertionConsumerUrl?: string;
  samlMetadataUrl?: string;
  /** Preset logos the API serves; staff can also upload their own. */
  icons?: SsoIconOption[];
}

export class SsoProviderService extends BaseApiService {
  private static instance: SsoProviderService;

  static getInstance(): SsoProviderService {
    SsoProviderService.instance ??= new SsoProviderService();
    return SsoProviderService.instance;
  }

  async getSetup(): Promise<SsoSetup> {
    const response = await this.get<{ data: SsoSetup }>("/sso/setup", getApiConfig().core);
    return response.data;
  }

  /** Every customer's providers, for the staff overview. */
  async getAllProviders(): Promise<CustomerSsoProvider[]> {
    const response = await this.get<{ data: CustomerSsoProvider[] }>(
      "/ssoProvider",
      getApiConfig().core,
    );
    return response.data;
  }

  async getProviders(customerId: number): Promise<CustomerSsoProvider[]> {
    const response = await this.get<{ data: CustomerSsoProvider[] }>(
      `/customer/${customerId}/ssoProvider`,
      getApiConfig().core,
    );
    return response.data;
  }

  async saveProvider(
    customerId: number,
    providerId: number | null,
    input: SsoProviderInput,
  ): Promise<CustomerSsoProvider> {
    // Send only editable fields. Customer ownership is always supplied by the URL.
    // The secret is omitted when blank so an edit keeps the stored value.
    const body: SsoProviderBody = {
      name: input.name.trim(),
      protocol: input.protocol,
      issuerUri: input.issuerUri.trim(),
      supportsWeb: input.supportsWeb,
      supportsIos: input.supportsIos,
      supportsAndroid: input.supportsAndroid,
      redirectUri: input.redirectUri.trim() || null,
      enabled: input.enabled,
      iconUrl: input.iconUrl.trim() || null,
    };
    if (input.protocol === "SAML") {
      // SAML has no token endpoint, so the client fields do not apply.
      body.samlMetadataUrl = input.samlMetadataUrl.trim();
    } else {
      body.clientId = input.clientId.trim();
      body.tokenEndpointAuthMethod = input.tokenEndpointAuthMethod;
      if (input.tokenEndpointAuthMethod !== "none" && input.clientSecret.trim()) {
        body.clientSecret = input.clientSecret;
      }
    }
    const path = `/customer/${customerId}/ssoProvider`;
    const response =
      providerId === null
        ? await this.post<{ data: CustomerSsoProvider }>(path, body, getApiConfig().core)
        : await this.put<{ data: CustomerSsoProvider }>(
            `${path}/${providerId}`,
            body,
            getApiConfig().core,
          );
    return response.data;
  }

  /** Uploads a custom logo to the environment's asset bucket and returns its public URL. */
  async uploadIcon(file: File): Promise<string> {
    const authHeader = this.coreService.getAuthorizationHeader();
    if (!authHeader) throw new Error("You must be logged in to access this resource");
    const token = authHeader.startsWith("Bearer ") ? authHeader : `Bearer ${authHeader}`;
    const formData = new FormData();
    formData.append("image", file);
    // Not fetchWithAuth: the browser must set the multipart content type and boundary itself.
    const response = await fetch(`${getApiConfig().core}/photo/upload`, {
      method: "POST",
      headers: { Accept: "application/json", Authorization: token },
      body: formData,
    });
    const payload = (await response.json().catch(() => null)) as {
      data?: unknown;
      metadata?: { errors?: string[] };
    } | null;
    if (!response.ok || typeof payload?.data !== "string") {
      throw new Error(payload?.metadata?.errors?.[0] || "Unable to upload the logo");
    }
    return payload.data;
  }

  async deleteProvider(customerId: number, providerId: number): Promise<void> {
    await this.delete(`/customer/${customerId}/ssoProvider/${providerId}`, getApiConfig().core);
  }
}
